import { html } from 'lit-html';
import { CardStack } from '@horse-tinder/card-stack';
import {
  renderLandingCard,
  renderHorseCard,
  renderEndCard,
  renderLeftOverlay,
  renderRightOverlay,
} from './templates.js';
import { horses } from './data.js';
import '@horse-tinder/card-stack/styles.css';
import './styles.css';

/**
 * Horse Tinder App
 * Demonstrates CardStack flexibility with a completely different use case
 */

/**
 * Extract average color from bottom portion of an image
 * @param {string} imageUrl - URL of the image
 * @returns {Promise<string>} - Hex color string
 */
function extractImageColor(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Use small size for performance
      const size = 50;
      canvas.width = size;
      canvas.height = size;

      // Draw image scaled down
      ctx.drawImage(img, 0, 0, size, size);

      // Sample bottom third of image (where the gradient overlay is)
      const startY = Math.floor(size * 0.66);
      const imageData = ctx.getImageData(0, startY, size, size - startY);
      const data = imageData.data;

      let r = 0, g = 0, b = 0, count = 0;

      for (let i = 0; i < data.length; i += 4) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }

      // Average and darken for better text contrast
      r = Math.floor((r / count) * 0.4);
      g = Math.floor((g / count) * 0.4);
      b = Math.floor((b / count) * 0.4);

      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      resolve(hex);
    };

    img.onerror = () => {
      // Fallback color on error
      resolve('#1a1510');
    };

    img.src = imageUrl;
  });
}

/**
 * Generate gradient from a base color
 */
function colorToGradient(hex) {
  // Parse hex
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Create slightly lighter version for gradient start
  const lighterR = Math.min(255, Math.floor(r * 1.3));
  const lighterG = Math.min(255, Math.floor(g * 1.3));
  const lighterB = Math.min(255, Math.floor(b * 1.3));

  const lighter = `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;

  return `linear-gradient(135deg, ${lighter}, ${hex})`;
}

/**
 * Application State
 */
const state = {
  items: [],
  liked: new Set(),
  passed: new Set(),
};

/**
 * Build items array with landing + horses + end
 */
function buildItems() {
  state.items = [
    { type: 'landing', bg: 'linear-gradient(135deg, #2d1b0e, #1a1510)' },
    ...horses.map(horse => ({ type: 'horse', ...horse })),
    { type: 'end', bg: 'linear-gradient(135deg, #1a3a2e, #0f1f18)' },
  ];
  return state.items;
}

/**
 * Calculate colors for all horses from their images
 */
async function calculateHorseColors() {
  const colorPromises = horses.map(horse => extractImageColor(horse.image));
  const colors = await Promise.all(colorPromises);

  // Update each horse's bg with calculated gradient
  colors.forEach((color, i) => {
    const gradient = colorToGradient(color);
    horses[i].bg = gradient;

    // Also update in state.items (horse is at index i+1 due to landing page)
    if (state.items[i + 1]) {
      state.items[i + 1].bg = gradient;
    }
  });

  // Re-render stack with new colors
  stack.setItems(state.items);
  updateBodyBackground();
}

/**
 * Get stats
 */
function getStats() {
  return {
    total: horses.length,
    liked: state.liked.size,
    passed: state.passed.size,
  };
}

/**
 * Render card based on type
 */
function renderCard(item, index) {
  if (item.type === 'landing') {
    return renderLandingCard(item, getStats());
  }

  if (item.type === 'end') {
    return renderEndCard(getStats());
  }

  // Horse card
  const isLiked = state.liked.has(item.id);
  const isPassed = state.passed.has(item.id);
  return renderHorseCard(item, index, isLiked, isPassed);
}

/**
 * Get card configuration
 */
function getCardConfig(item, index) {
  // Landing and end pages - no horizontal swipes
  if (item.type === 'landing' || item.type === 'end') {
    return {
      swipeLeft: false,
      swipeRight: false,
      background: item.bg,
    };
  }

  // Already decided - no swipes
  if (state.liked.has(item.id) || state.passed.has(item.id)) {
    return {
      swipeLeft: false,
      swipeRight: false,
      background: item.bg,
    };
  }

  // Horse card - can like/pass
  return {
    swipeLeft: 'pass',
    swipeRight: 'like',
    background: item.bg,
  };
}

/**
 * Find next undecided horse
 */
function findNextUndecided(items, current) {
  const lastHorseIndex = items.length - 2;

  // Look after current
  for (let i = current + 1; i <= lastHorseIndex; i++) {
    const item = items[i];
    if (item.type === 'horse' && !state.liked.has(item.id) && !state.passed.has(item.id)) {
      return i;
    }
  }

  // Wrap around
  for (let i = 1; i < current; i++) {
    const item = items[i];
    if (item.type === 'horse' && !state.liked.has(item.id) && !state.passed.has(item.id)) {
      return i;
    }
  }

  return -1;
}

/**
 * Initialize CardStack
 */
const stack = new CardStack(document.getElementById('app'), {
  items: buildItems(),
  renderCard,
  getCardConfig,
  findNextTarget: findNextUndecided,
  renderLeftOverlay,
  renderRightOverlay,
});

/**
 * Handle like (swipe right)
 */
stack.on('swipe-right', ({ item, index }) => {
  console.log('Liked:', item.name);
  state.liked.add(item.id);

  // Update card to show liked state
  stack.updateItem(index, item);
  updateCounter();
  updateLandingPage();
});

/**
 * Handle pass (swipe left)
 */
stack.on('swipe-left', ({ item, index }) => {
  console.log('Passed:', item.name);
  state.passed.add(item.id);

  // Update card to show passed state
  stack.updateItem(index, item);
  updateCounter();
  updateLandingPage();
});

/**
 * Play video for the current horse card, fade out when done
 */
function playCardVideo() {
  const item = stack.getCurrentItem();
  if (item?.type !== 'horse') return;

  const idx = stack.getCurrentIndex();
  const cardEl = document.querySelector(`.card-stack-card[data-index="${idx}"]`);
  if (!cardEl) return;

  const video = cardEl.querySelector('.horse-video');
  if (!video) return;

  // Reset state for replay
  video.classList.remove('fade-out');
  video.currentTime = 0;

  video.onended = () => {
    video.classList.add('fade-out');
  };

  video.onerror = () => {
    // If video fails to load, just hide it
    video.classList.add('fade-out');
  };

  video.play().catch(() => {
    // Autoplay blocked or file missing — fade to image
    video.classList.add('fade-out');
  });
}

/**
 * Handle navigation
 */
stack.on('navigate', ({ from, to }) => {
  updateCounter();
  updateBodyBackground();
  playCardVideo();
});

/**
 * Update landing page stats
 */
function updateLandingPage() {
  stack.updateItem(0, state.items[0]);
  // Also update end card
  stack.updateItem(state.items.length - 1, state.items[state.items.length - 1]);
}

/**
 * Update counter display
 */
function updateCounter() {
  const counter = document.getElementById('counter');
  if (!counter) return;

  const current = stack.getCurrentIndex();
  const item = stack.getCurrentItem();

  // Hide on landing and end pages
  if (item.type === 'landing' || item.type === 'end') {
    counter.classList.add('hidden');
    return;
  }

  counter.classList.remove('hidden');
  const horseIndex = current; // 1-indexed due to landing page
  counter.innerHTML = `<span>${horseIndex}</span> / ${horses.length}`;
}

/**
 * Update body background
 */
function updateBodyBackground() {
  const bg = stack.getCurrentItem()?.bg;
  if (bg) {
    document.documentElement.style.setProperty('--card-bg', bg);

    const match = bg.match(/#[0-9a-fA-F]{6}/g);
    if (match) {
      document.documentElement.style.setProperty('--card-bg-color', match[match.length - 1]);
    }
  }
}

// Initial setup
updateCounter();
updateBodyBackground();

// Calculate colors from images (async, updates after load)
calculateHorseColors();

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/horses-sw.js');
}

console.log('Horse Tinder initialized with', horses.length, 'horses');
